'use client'

import { C, Callout, Exercise, H, Lesson, P, Pre, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/neural-nets'

export default function Autograd() {
  return (
    <Lesson>
      <Step>
        <H>Numbers with a memory</H>
        <P>
          Doing backprop by hand is fine for one formula. Real networks have millions of operations. So we automate it with a
          beautifully simple trick: wrap every number in an object that remembers <strong>which numbers it was made from</strong>{' '}
          and <strong>how to pass a gradient back to them</strong>.
        </P>
        <P>
          Every time you add or multiply two of these objects, the result records its parents and a tiny “backward step”
          function. The calculation builds its own computational graph as it runs. Afterwards, one call to{' '}
          <C>backward()</C> walks that graph in reverse and fills in every gradient.
        </P>
        <P>
          This is <strong>automatic differentiation</strong> (“autograd”), and it’s the engine inside PyTorch, JAX and
          TensorFlow. The version you’re about to write is the same idea in about 60 lines.
        </P>
      </Step>

      <Step gate="q-topo">
        <H>The one subtle part: order</H>
        <P>
          A node can only pass its gradient back once it has received <em>all</em> of its own gradient — from every place it was
          used. So we need an ordering where every node comes after everything it feeds into, when walking backwards. That’s a{' '}
          <strong>topological order</strong>, and a short recursive function builds it:
        </P>
        <Pre>{`const order: Value[] = []
const seen = new Set<Value>()
function visit(v: Value) {
  if (seen.has(v)) return
  seen.add(v)
  for (const child of v.children) visit(child)   // inputs first…
  order.push(v)                                  // …then the node itself
}
visit(output)
// now walk order from the END (the output) back to the start`}</Pre>
        <Quiz
          id="q-topo"
          question={<>In <C>b = a.add(a)</C>, the value <C>a</C> is used twice. What should <C>a.grad</C> be after <C>b.backward()</C>?</>}
          options={[
            { label: '2 — both uses contribute, so gradients are added (+=), not overwritten', correct: true },
            { label: '1', why: 'If you write = instead of +=, the second contribution overwrites the first. A classic autograd bug.' },
            { label: '0', why: 'b changes 2-for-1 with a: if a goes up by 0.1, b goes up by 0.2.' },
          ]}
          explain="Gradients from every use accumulate. That’s why each backwardStep uses += — and why you must reset grads to 0 before each training step."
        />
      </Step>

      <Step gate="autograd">
        <H>Build your autograd engine</H>
        <P>
          <C>add</C> is done as an example. Finish <C>mul</C>, <C>tanh</C> and <C>backward</C>. When it works, the neuron at the
          bottom prints the same gradients as the backprop graph you stepped through by hand.
        </P>
        <Exercise
          id="autograd"
          title="class Value"
          {...EX.autograd}
          timeoutMs={8000}
          hints={[
            <>
              mul’s forward value is <C>this.data * other.data</C>. Its backward step: <C>this.grad += other.data * out.grad</C>{' '}
              and <C>other.grad += this.data * out.grad</C>.
            </>,
            <>
              tanh’s backward step: <C>this.grad += (1 - t * t) * out.grad</C>.
            </>,
            <>
              In backward: build <C>order</C> with the visit function shown above, set <C>this.grad = 1</C>, then loop{' '}
              <C>{'for (let i = order.length - 1; i >= 0; i--) order[i].backwardStep()'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>The built-in version</H>
        <P>
          From now on you can use the built-in <C>Value</C> class — it’s exactly what you wrote, plus <C>sub</C>, <C>div</C>,{' '}
          <C>pow</C>, <C>exp</C>, <C>log</C>, <C>relu</C> and <C>sigmoid</C>. There are also <C>Neuron</C>, <C>Layer</C> and{' '}
          <C>MLP</C> classes built on top of it. Hover over them in the editor to read their docs.
        </P>
        <Exercise id="builtin-value" title="The built-in Value" {...EX.builtinValue} />
        <Callout kind="why">
          You’ve now built, from nothing, the core of a deep-learning framework. Everything else — GPUs, tensors, clever
          optimizers — is about doing this faster and at larger scale. Next: training a real network with it.
        </Callout>
      </Step>
    </Lesson>
  )
}
